const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8081/api';

export class AdminProjectApiError extends Error {
  status: number;
  code?: string;
  body: unknown;

  constructor(message: string, status: number, code?: string, body?: unknown) {
    super(message);
    this.name = 'AdminProjectApiError';
    this.status = status;
    const errorBody = body && typeof body === 'object'
      ? body as { code?: string; error?: string }
      : undefined;
    this.code = code ?? errorBody?.code ?? errorBody?.error;
    this.body = body;
  }
}

function getToken(): string {
  return localStorage.getItem('token') || '';
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const token = getToken();
  const response = await fetch(`${API_BASE}${url}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
    ...options,
  });
  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
    throw new Error('未登录');
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' })) as {
      error?: string;
      code?: string;
      message?: string;
    };
    throw new AdminProjectApiError(
      error.message || error.error || 'Request failed',
      response.status,
      error.code ?? error.error,
      error,
    );
  }
  return response.json();
}

export interface AdminProjectListItem {
  id: string;
  name: string;
  ownerId: string;
  ownerName?: string;
  createdAt: string;
}

export interface AdminListProjectsParams {
  q?: string;
  ownerId?: string;
  page?: number;
  limit?: number;
}

export interface AdminListProjectsResp {
  data: AdminProjectListItem[];
  meta: { total: number; page: number; limit: number };
}

export const adminListProjects = (
  params: AdminListProjectsParams = {},
): Promise<AdminListProjectsResp> => {
  const search = new URLSearchParams();
  if (params.q) search.set('q', params.q);
  if (params.ownerId) search.set('ownerId', params.ownerId);
  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  const qs = search.toString();
  return request(`/admin/projects${qs ? `?${qs}` : ''}`);
};

export const adminTransferProjectOwner = (
  projectId: string,
  newOwnerId: string,
): Promise<{ success: boolean }> =>
  request(`/admin/projects/${encodeURIComponent(projectId)}/transfer-owner`, {
    method: 'POST',
    body: JSON.stringify({ newOwnerId }),
  });

// adminResetProject clears all script-derived content (scripts, episodes,
// shots, versions, resources, analyses, asset/image/voice/subtitle tasks) while
// preserving the project itself (owner, base-image config, skill config),
// members, tickets, favorites, and financial audit records. The backend also
// validates that projectName matches the stored name — it is the caller's
// second-step confirmation.
export const adminResetProject = (
  projectId: string,
  projectName: string,
): Promise<{ success: boolean }> =>
  request(`/admin/projects/${encodeURIComponent(projectId)}/reset`, {
    method: 'POST',
    body: JSON.stringify({ projectName }),
  });

export interface ShotMigrationError {
  shotId: string;
  error: string;
}

export interface MigrationSample {
  shotId: string;
  before: string;
  after: string;
}

export interface TokenMigrationStats {
  dryRun: boolean;
  totalShots: number;
  shotsWithLegacy: number;
  shotsWouldChange: number;
  migratedCount: number;
  failedCount: number;
  danglingCount: number;
  changedShotIds: string[];
  danglingShotIds: string[];
  errors: ShotMigrationError[];
  truncated: boolean;
  sample?: MigrationSample[];
}

// 历史 @N → 类型化 token 迁移。confirmed=false 为 dry-run（只统计不落库）。
// 后端可能返回 409（项目下有集正在匹配/生成），request() 会抛带后端 error 文案的 Error。
export const migrateProjectTypedTokens = (
  projectId: string,
  confirmed: boolean,
): Promise<TokenMigrationStats> =>
  request(`/admin/projects/${encodeURIComponent(projectId)}/migrate-typed-tokens`, {
    method: 'POST',
    body: JSON.stringify({ confirmed }),
  });

export interface AssetMergeVariant {
  name: string;
  entryCount: number;
}

export interface AssetMergeGroupStat {
  canonicalName: string;
  variants: AssetMergeVariant[];
  shotAssetRefs: number;
  versionRefs: number;
  reviewRows: number;
  styleTests: number;
  styleResults: number;
  imageTasks: number;
  searchRenames: number;
  searchDeletes: number;
  castingDeletes: number;
  stylingDeletes: number;
}

export interface AssetMergeBlockedGroup {
  canonicalName: string;
  reason: string;
}

export interface AssetMergeStats {
  dryRun: boolean;
  totalGroups: number;
  totalVariants: number;
  mergedGroups: number;
  skippedGroups: number;
  groups: AssetMergeGroupStat[];
  blocked: AssetMergeBlockedGroup[];
  assetJsonRebuilt: boolean;
  advancedAnalysisInProgress: boolean;
}

// 人物资产大小写合并。confirmed=false 为 dry-run（只统计不落库）。
// 后端可能返回 409（高级分析进行中），request() 会抛带后端 error 文案的 Error。
export const mergeProjectAssetCase = (
  projectId: string,
  confirmed: boolean,
): Promise<AssetMergeStats> =>
  request(`/admin/projects/${encodeURIComponent(projectId)}/merge-asset-case`, {
    method: 'POST',
    body: JSON.stringify({ confirmed }),
  });
